/**
 * OpenAI Model Pricing Configuration
 * Prices are in USD per 1 million tokens
 * Last updated: December 2025
 *
 * Sources:
 * - https://openai.com/api/pricing/
 * - https://pricepertoken.com/pricing-page/model/openai-gpt-5
 * - https://skywork.ai/blog/ai-agent/gpt5-1-api-pricing/
 */

export interface ModelPricing {
  inputPricePerMillion: number;
  outputPricePerMillion: number;
  displayName: string;
}

/**
 * Pricing table for OpenAI models
 * Keys should match model names as stored in the database
 */
export const MODEL_PRICING: Record<string, ModelPricing> = {
  // GPT-5.1 models (current flagship - released Nov 2025)
  // Same pricing as GPT-5: $1.25 input, $10 output per 1M tokens
  "gpt-5.1": {
    inputPricePerMillion: 1.25,
    outputPricePerMillion: 10.00,
    displayName: "GPT-5.1"
  },
  "gpt-5.1-chat-latest": {
    inputPricePerMillion: 1.25,
    outputPricePerMillion: 10.00,
    displayName: "GPT-5.1 Chat"
  },

  // GPT-5 models (released Aug 2025)
  "gpt-5": {
    inputPricePerMillion: 1.25,
    outputPricePerMillion: 10.00,
    displayName: "GPT-5"
  },

  // GPT-5 mini models - faster/cheaper tier
  "gpt-5-mini": {
    inputPricePerMillion: 0.25,
    outputPricePerMillion: 2.00,
    displayName: "GPT-5 Mini"
  },

  // GPT-5 nano models - fastest/cheapest tier
  "gpt-5-nano": {
    inputPricePerMillion: 0.05,
    outputPricePerMillion: 0.40,
    displayName: "GPT-5 Nano"
  },

  // GPT-4o models (previous generation)
  "gpt-4o": {
    inputPricePerMillion: 2.50,
    outputPricePerMillion: 10.00,
    displayName: "GPT-4o"
  },
  "gpt-4o-2024-11-20": {
    inputPricePerMillion: 2.50,
    outputPricePerMillion: 10.00,
    displayName: "GPT-4o (Nov 2024)"
  },
  "gpt-4o-2024-08-06": {
    inputPricePerMillion: 2.50,
    outputPricePerMillion: 10.00,
    displayName: "GPT-4o (Aug 2024)"
  },

  // GPT-4o mini models
  "gpt-4o-mini": {
    inputPricePerMillion: 0.15,
    outputPricePerMillion: 0.60,
    displayName: "GPT-4o Mini"
  },
  "gpt-4o-mini-2024-07-18": {
    inputPricePerMillion: 0.15,
    outputPricePerMillion: 0.60,
    displayName: "GPT-4o Mini (Jul 2024)"
  },

  // GPT-4 Turbo (legacy)
  "gpt-4-turbo": {
    inputPricePerMillion: 10.00,
    outputPricePerMillion: 30.00,
    displayName: "GPT-4 Turbo"
  },
  "gpt-4-turbo-2024-04-09": {
    inputPricePerMillion: 10.00,
    outputPricePerMillion: 30.00,
    displayName: "GPT-4 Turbo (Apr 2024)"
  },

  // GPT-4 (legacy)
  "gpt-4": {
    inputPricePerMillion: 30.00,
    outputPricePerMillion: 60.00,
    displayName: "GPT-4"
  },

  // GPT-3.5 Turbo (legacy)
  "gpt-3.5-turbo": {
    inputPricePerMillion: 0.50,
    outputPricePerMillion: 1.50,
    displayName: "GPT-3.5 Turbo"
  },
  "gpt-3.5-turbo-0125": {
    inputPricePerMillion: 0.50,
    outputPricePerMillion: 1.50,
    displayName: "GPT-3.5 Turbo (Jan 2025)"
  },
};

/**
 * Default pricing for unknown models (uses GPT-5.1 pricing as the primary model)
 */
export const DEFAULT_PRICING: ModelPricing = {
  inputPricePerMillion: 1.25,
  outputPricePerMillion: 10.00,
  displayName: "Unknown Model"
};

/**
 * Get pricing for a model, falling back to default if not found
 */
export function getModelPricing(modelName: string): ModelPricing {
  // Try exact match first
  if (MODEL_PRICING[modelName]) {
    return MODEL_PRICING[modelName];
  }

  // Try case-insensitive match
  const lowerModel = modelName.toLowerCase();
  for (const [key, pricing] of Object.entries(MODEL_PRICING)) {
    if (key.toLowerCase() === lowerModel) {
      return pricing;
    }
  }

  // Try partial match for versioned models
  for (const [key, pricing] of Object.entries(MODEL_PRICING)) {
    if (lowerModel.startsWith(key.toLowerCase()) || key.toLowerCase().startsWith(lowerModel)) {
      return pricing;
    }
  }

  return DEFAULT_PRICING;
}

/**
 * Calculate cost for a single LLM request
 * @param modelName - The model used
 * @param promptTokens - Number of input tokens
 * @param completionTokens - Number of output tokens
 * @returns Cost in USD
 */
export function calculateCost(
  modelName: string,
  promptTokens: number | null,
  completionTokens: number | null
): number | null {
  if (promptTokens === null && completionTokens === null) {
    return null;
  }

  const pricing = getModelPricing(modelName);
  const inputCost = (promptTokens || 0) * (pricing.inputPricePerMillion / 1_000_000);
  const outputCost = (completionTokens || 0) * (pricing.outputPricePerMillion / 1_000_000);

  return inputCost + outputCost;
}

/**
 * Calculate aggregated cost using default pricing (for rollups across potentially mixed models)
 * @deprecated Use getTotalCostFromApi() instead - the API now provides pre-calculated costs from the database
 * @param promptTokens - Total number of input tokens
 * @param completionTokens - Total number of output tokens
 * @returns Cost in USD or null if no tokens
 */
export function calculateAggregatedCost(
  promptTokens: number | null,
  completionTokens: number | null
): number | null {
  if (promptTokens === null && completionTokens === null) {
    return null;
  }

  const inputCost = (promptTokens || 0) * (DEFAULT_PRICING.inputPricePerMillion / 1_000_000);
  const outputCost = (completionTokens || 0) * (DEFAULT_PRICING.outputPricePerMillion / 1_000_000);

  return inputCost + outputCost;
}

/**
 * Get total cost from API-provided input and output costs
 * This uses the costs calculated at the time of the LLM request (stored in database)
 * which are more accurate than recalculating based on tokens alone.
 * @param inputCostUsd - Input cost in USD (from API)
 * @param outputCostUsd - Output cost in USD (from API)
 * @returns Total cost in USD or null if no costs available
 */
export function getTotalCostFromApi(
  inputCostUsd: number | null,
  outputCostUsd: number | null
): number | null {
  if (inputCostUsd === null && outputCostUsd === null) {
    return null;
  }

  return (inputCostUsd || 0) + (outputCostUsd || 0);
}

/**
 * Format cost for display
 * @param cost - Cost in USD
 * @returns Formatted cost string
 */
export function formatCost(cost: number | null): string {
  if (cost === null) return 'N/A';
  if (cost < 0.01) return `$${cost.toFixed(6)}`;
  if (cost < 1) return `$${cost.toFixed(4)}`;
  return `$${cost.toFixed(2)}`;
}
